import { useState } from 'react';
import { line, Color, Size, t, type VcardImportPreview, type VcardImportReport, type VcardImportRow, type VcardRowChoice, type VcardSource } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Button } from '../../components/controls/Button';
import { Modal } from '../../components/modals/Modal';
import { PAGES_PATH, post } from './api';
import styles from './VcardImportModal.module.css';

/**
 * Import vCard on the People list (piece 13, decisions 0341 and 0617). One or
 * many `.vcf` files are read in the browser and sent as text; the API answers a
 * preview naming what each card would do — create, merge or skip, the Company it
 * matched and the site its address names — and nothing is written until Import.
 *
 * 🔒 A row that matched somebody already here reads as a warning with a choice.
 * Create new or Merge, and Import stays refused while any row is unanswered
 * (decision 0617). An exact match arrives on Merge; a name-only match arrives on
 * neither, because two people can share a name.
 *
 * 🔨 Relative to the API root, never prefixed with /api.
 */
export const VCARD_IMPORT_PATH = `${PAGES_PATH}/people/import-vcard`;

export interface VcardImportModalProps {
  open: boolean;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** Called once a commit has landed, so the list reloads. */
  onImported: (report: VcardImportReport) => void;
  openerId?: string | null;
}

async function readText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('unreadable'));
    reader.readAsText(file);
  });
}

function actionLabel(row: VcardImportRow): string {
  // A row still waiting on its answer says so, rather than reading as the outcome the server would default to.
  if (row.choiceRequired && row.choice === null) return t('vcard.action.choose');
  return row.action === 'create' ? t('vcard.action.create') : row.action === 'update' ? t('vcard.action.update') : t('vcard.action.skip');
}

function companyCell(row: VcardImportRow): string {
  if (row.companyAction === 'own') return `${row.organization ?? ''} · ${t('vcard.company.own')}`;
  if (row.companyAction === 'create') return `${row.organization ?? ''} · ${t('vcard.company.new')}`;
  if (row.company) return row.company.displayName;
  return row.organization ?? '';
}

/** What the address column says: the line itself, and what the commit will do with it. */
function addressCell(row: VcardImportRow): string {
  if (!row.address) return '';
  if (row.siteAction === 'match') return `${row.address} · ${t('vcard.site.match')}`;
  if (row.siteAction === 'create') return `${row.address} · ${t('vcard.site.new')}`;
  return `${row.address} · ${t('vcard.site.none')}`;
}

export function VcardImportModal({ open, apiBaseUrl = '', onCancel, onImported, openerId = null }: VcardImportModalProps) {
  const [sources, setSources] = useState<readonly VcardSource[] | null>(null);
  const [rows, setRows] = useState<readonly VcardImportRow[] | null>(null);
  const [choices, setChoices] = useState<Record<number, VcardRowChoice>>({});
  const [report, setReport] = useState<VcardImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const chose = async (files: readonly File[]) => {
    setError(null);
    setReport(null);
    setRows(null);
    setChoices({});
    if (files.length === 0) return;
    setBusy(true);
    // 🔨 Every chosen file is read and sent with its own name, so a preview row can say where the card came from.
    const read: VcardSource[] = [];
    for (const file of files) read.push({ filename: file.name, text: await readText(file) });
    setSources(read);
    const answer = await post<VcardImportPreview>(`${VCARD_IMPORT_PATH}/preview`, { files: read }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    setRows(answer.data.rows);
    // The defaults the server decided — Merge on an exact match — are what the screen starts from.
    setChoices(Object.fromEntries(answer.data.rows.filter((row) => row.choice !== null).map((row) => [row.index, row.choice!])));
  };

  /** An answer is sent straight back for a fresh preview, so the row redraws what it will do. */
  const answer = async (index: number, choice: VcardRowChoice) => {
    const next = { ...choices, [index]: choice };
    setChoices(next);
    if (!sources) return;
    setBusy(true);
    const again = await post<VcardImportPreview>(`${VCARD_IMPORT_PATH}/preview`, { files: sources, choices: next }, apiBaseUrl);
    setBusy(false);
    if (!again.ok) return setError(again.message);
    setRows(again.data.rows);
  };

  const commit = async () => {
    if (!sources) return;
    setError(null);
    setBusy(true);
    const answered = await post<VcardImportReport>(VCARD_IMPORT_PATH, { files: sources, choices }, apiBaseUrl);
    setBusy(false);
    if (!answered.ok) return setError(answered.message);
    setReport(answered.data);
    setRows(answered.data.rows);
    onImported(answered.data);
  };

  const importable = rows ? rows.filter((row) => row.action !== 'skip').length : 0;
  /** 🔒 Import is refused while any row still owes an answer; the count of them is what the strip says. */
  const unanswered = rows ? rows.filter((row) => row.choiceRequired && (choices[row.index] ?? null) === null).length : 0;
  const manyFiles = (sources?.length ?? 0) > 1;
  const summary = report
    ? `${report.created} ${t('vcard.report.created')} · ${report.updated} ${t('vcard.report.updated')} · ${report.skipped} ${t('vcard.report.skipped')} · ${report.companiesCreated} ${t('vcard.report.companies')} · ${report.sitesCreated} ${t('vcard.report.sites')}`
    : null;

  return (
    <Modal
      open={open}
      title={t('vcard.title')}
      subtitle={t('vcard.subtitle')}
      onCancel={onCancel}
      openerId={openerId}
      width="wide"
      dataModal="import-vcard"
      actions={
        report
          ? [
              // 🔒 Decision 2140: the report writes nothing more, so it carries one Done and no Close beside it (decision 3063).
              { kind: 'close', label: t('vcard.done'), onAct: onCancel },
            ]
          : [
              {
                kind: 'primary',
                label: busy ? t('vcard.importing') : `${t('vcard.import')} ${importable || ''}`.trim(),
                onAct: () => void commit(),
                disabled: busy || importable === 0 || unanswered > 0,
                ...(unanswered > 0 ? { disabledReason: t('vcard.choose.blocked') } : {}),
              },
              { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
            ]
      }
    >
      <div data-vcard-import>
        {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
        {summary ? <AlertBanner tone="good" message={summary} /> : null}
        {/*
          🔒 The count of waiting rows is stated, so the refused Import is never a
          mystery. 🔨 Not an Alert Banner: a banner that states a problem owes an
          action (piece 3), and the only action here is the per-row choice the owner
          asked for by name. A bulk Merge All would undo the choice this is for.
        */}
        {!report && unanswered > 0 ? <p className={styles.blocked} data-vcard-blocked={String(unanswered)}>{line(String(unanswered), t('vcard.choose.waiting'))}</p> : null}
        {!report ? (
          <label className={styles.picker}>
            <span className={styles.pickerLabel}>{t('vcard.file')}</span>
            {/* T98: several .vcf files at once, read in the browser and previewed as one table. */}
            <input type="file" multiple accept=".vcf,text/vcard,text/x-vcard" data-vcard-file onChange={(event) => void chose([...(event.target.files ?? [])])} disabled={busy} />
            <span className={styles.hint}>{busy && !rows ? t('vcard.reading') : t('vcard.choose')}</span>
          </label>
        ) : null}
        {rows && rows.length === 0 ? <p className={styles.hint}>{t('vcard.empty')}</p> : null}
        {rows && rows.length > 0 ? (
          // A row per card, not a bare table: every table in the application is the List Viewer's, and a preview inside a modal is a list.
          <ul className={styles.rows} data-vcard-preview>
            <li className={styles.head} aria-hidden="true">
              <span>{t('vcard.column.name')}</span>
              <span>{t('vcard.column.company')}</span>
              <span>{t('vcard.column.email')}</span>
              <span>{t('vcard.column.phone')}</span>
              <span>{t('vcard.column.address')}</span>
              <span>{t('vcard.column.action')}</span>
              <span>{t('vcard.column.reason')}</span>
            </li>
            {rows.map((row) => {
              const chosen = choices[row.index] ?? null;
              const waiting = row.choiceRequired && chosen === null;
              return (
                <li key={row.index} className={styles.card} data-vcard-row={row.action} data-vcard-waiting={waiting ? 'true' : 'false'} data-vcard-index={row.index}>
                  <span className={styles.name}>
                    {[row.name, row.title].filter(Boolean).join(' · ')}
                    {manyFiles && row.sourceFile ? <span className={styles.source} data-vcard-source={row.sourceFile}>{row.sourceFile}</span> : null}
                  </span>
                  <span>{companyCell(row)}</span>
                  <span>{row.emails.join(', ')}</span>
                  <span>{row.phones.join(', ')}</span>
                  <span data-vcard-address={row.siteAction}>{addressCell(row)}</span>
                  <span className={styles.action}>{actionLabel(row)}</span>
                  <span className={styles.reason}>
                    {row.reason}
                    {/* T97: a close match is a warning with a choice the person makes, never a decision taken for them. */}
                    {row.choiceRequired ? (
                      <span className={styles.match} data-vcard-match={row.matchTier ?? 'none'}>
                        <span className={styles.matchName}>{row.person?.name}</span>
                        {row.personCompanies.length > 0 ? <span className={styles.matchDetail}>{line(t('vcard.match.at'), row.personCompanies.join(', '))}</span> : null}
                        {row.personEmails.length > 0 ? <span className={styles.matchDetail}>{row.personEmails.join(', ')}</span> : null}
                        <span className={styles.choice}>
                          <Button
                            size={Size.Small}
                            color={chosen === 'create' ? Color.Navy : Color.Inactive}
                            text={[t('vcard.choice.create')]}
                            actions={[() => void answer(row.index, 'create')]}
                            disabled={busy || report !== null}
                            {...(report !== null ? { disabledReason: t('vcard.choice.done') } : {})}
                          />
                          <Button
                            size={Size.Small}
                            color={chosen === 'merge' ? Color.Navy : Color.Inactive}
                            text={[t('vcard.choice.merge')]}
                            actions={[() => void answer(row.index, 'merge')]}
                            disabled={busy || report !== null}
                            {...(report !== null ? { disabledReason: t('vcard.choice.done') } : {})}
                          />
                        </span>
                        {waiting ? <span className={styles.waiting}>{t('vcard.choose.row')}</span> : null}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </Modal>
  );
}
